import { useState, useCallback, useRef, useMemo, useEffect } from "react";

import { HugeiconsIcon } from '@hugeicons/react';
import { Moon02Icon as Moon, Search01Icon as Search, Sun02Icon as Sun } from '@hugeicons/core-free-icons';
import { NotificationCenter } from "@/components/notifications/NotificationCenter";
import { Button } from "@/components/ui/button";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTheme } from "next-themes";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthFetch } from "@/hooks/useAuthFetch";

export function TopBar({ initialLoading = false }: { initialLoading?: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { theme, setTheme } = useTheme();
  const { id: frameId } = useParams<{ id?: string }>();
  const authFetch = useAuthFetch();

  // Determine if we're on a page builder view (legacy /frame kept for compatibility)
  const isFramePage = (location.pathname.startsWith('/page/') || location.pathname.startsWith('/frame/')) && frameId;

  const titleMap: Record<string, string> = {
    "/": "Home",
    "/pages": "Pages",
    "/pages/diagnostic": "Pages Diagnostic",
    "/pages/fresh-start": "Pages Fresh Start",
    "/frames": "Pages",
    "/frames/diagnostic": "Pages Diagnostic",
    "/frames/fresh-start": "Pages Fresh Start",
    "/page/builder": "Page Builder",
    "/frame/builder": "Page Builder",
    "/plugins": "Plugins",
    "/plugins/automations": "Automations",
    "/plugins/calendar": "Calendar",
    "/plugins/marketing": "Marketing",
    "/plugins/integrations": "Connectors",
    "/plugins/drive": "Drive",
    "/plugins/audit-log": "Audit Log",
    "/plugins/database": "Database",
    "/plugins/email": "Email",
    "/plugins/formflow": "Forms",
    "/plugins/whiteboard": "Whiteboard",
    "/apps": "Apps",
    "/apps/automations": "Automations",
    "/apps/calendar": "Calendar",
    "/apps/marketing": "Marketing",
    "/apps/connectors": "Connectors",
    "/apps/drive": "Drive",
    "/apps/audit-log": "Audit Log",
    "/apps/database": "Database",
    "/apps/email": "Email",
    "/apps/form-flow": "Forms",
    "/apps/whiteboard": "Whiteboard",
    "/apps/dataflow": "DataFlow",
    "/apps/matrix": "Matrix",
    "/apps/pulse-board": "Pulse Board",
    "/apps/luma": "Luma Gallery",
    "/apps/orbit": "Orbit Calendar",
    "/apps/chronoline": "ChronoLine",
    "/apps/prism": "Prism Analytics",
    "/apps/nexus": "Nexus Connectors",
    "/apps/app-builder": "App Builder",
    "/discover": "Discover",
    "/software": "Software Library",
    "/inbox": "Inbox",
    "/settings": "Settings",
  };

  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isSearchActive, setIsSearchActive] = useState(false);
  // Frame-related state removed — frame builder feature discontinued
  const fallback = location.pathname.split("/").filter(Boolean).map((s) => (s ? s[0].toUpperCase() + s.slice(1) : s)).join(" / ");
  
  const getPageTitle = (): string => {
    // If on a frame page, use path as title (frame builder discontinued)
    if (isFramePage) return 'Page';
    
    // Dynamic tab-based title for Discover
    if (location.pathname === '/discover') {
        const urlParams = new URLSearchParams(location.search);
        const tab = urlParams.get('tab') || 'overview';
        const tabMap: Record<string, string> = {
            'overview': 'Discover Overview',
            'apps': 'App Store',
            'connectors': 'Connectors Hub',
            'community': 'Community Feed',
            'themes': 'Theme Store'
        };
        return tabMap[tab] || 'Discover';
    }

    // Try exact match in titleMap
    if (titleMap[location.pathname]) return titleMap[location.pathname];
    
    // Try to match base path for routes like /plugins/something, /apps/something or /pages/something
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts.length > 0) {
      // Check for /plugins/* routes
      if (pathParts[0] === 'plugins' && pathParts.length > 1) {
        const pluginPath = `/plugins/${pathParts[1]}`;
        if (titleMap[pluginPath]) return titleMap[pluginPath];
      }

      // Check for /apps/* routes
      if (pathParts[0] === 'apps' && pathParts.length > 1) {
        const appPath = `/apps/${pathParts[1]}`;
        if (titleMap[appPath]) return titleMap[appPath];
      }
      
      // Check for /pages/* routes (legacy /frames handled below)
      if (pathParts[0] === 'pages' && pathParts.length > 1) {
        const pagePath = `/pages/${pathParts[1]}`;
        if (titleMap[pagePath]) return titleMap[pagePath];
      }

      // Check for /frames/* routes (legacy)
      if (pathParts[0] === 'frames' && pathParts.length > 1) {
        const framePath = `/frames/${pathParts[1]}`;
        if (titleMap[framePath]) return titleMap[framePath];
      }
    }
    
    // Fallback to generated title from path
    return fallback || "Home";
  };
  
  const pageTitle = getPageTitle();

  useEffect(() => {
    // Frame builder removed — no frame name loading needed
    // Listen for page title updates (kept for forward compatibility)
    const handleFrameUpdated = (event: Event) => {
      const customEvent = event as CustomEvent;
      // Frame updates are no-ops since frame builder is discontinued
    };
    window.addEventListener('GENOFX_FRAME_UPDATED', handleFrameUpdated);
    return () => window.removeEventListener('GENOFX_FRAME_UPDATED', handleFrameUpdated);
  }, [isFramePage, frameId]);

  // Dynamic Search — replaced with CommandMenu popup
  // Clicking the search bar or pressing \ opens the command palette
  const handleSearchFocus = useCallback(() => {
    // Dispatch event to open CommandMenu
    window.dispatchEvent(new Event("OPEN_COMMAND_MENU"));
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input/textarea (including contenteditable)
      const target = e.target as HTMLElement;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target.isContentEditable
      ) {
        return;
      }

      // Use backslash (\) instead of forward slash (/)
      if (e.key === "\\" || (e.key === "k" && (e.metaKey || e.ctrlKey))) {
        e.preventDefault();
        setIsSearchActive(true);
        // Small timeout to ensure render happens before focus if conditional rendering is involved
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header className="h-16 bg-sidebar text-foreground flex items-center px-4 gap-2 md:gap-4 sticky top-0 z-[100] transition-colors duration-200 border-b border-sidebar-border/60">
      <div className="flex items-center gap-4 flex-1">
        {/* Breadcrumbs - Desktop Only */}
        {!isMobile && (
          <div className="flex items-center text-sm text-muted-foreground">
            <span className="hover:text-foreground cursor-pointer transition-colors" onClick={() => navigate('/')}>Workspace</span>
            <span className="mx-2 text-muted-foreground/50">/</span>
            <span className="font-medium text-foreground">{pageTitle}</span>
          </div>
        )}
      </div>

      <div className={cn("flex-1 px-2 md:px-4 transition-all", isMobile && isSearchActive ? "absolute inset-x-0 bottom-0 top-0 z-50 flex items-center bg-background px-2" : "max-w-xl")}>
        <div className="relative w-full">
          {initialLoading ? (
            <Skeleton className="h-9 w-full rounded-[20px]" />
          ) : (
            <button
              onClick={handleSearchFocus}
              className="w-full flex items-center gap-2 px-3 h-9 bg-muted/50 rounded-[20px] text-sm text-muted-foreground hover:bg-muted/80 transition-all duration-200 cursor-pointer"
            >
              <HugeiconsIcon icon={Search} className="h-4 w-4 flex-shrink-0" strokeWidth={1.5}  />
              <span className="flex-1 text-left truncate">Search software, settings...</span>
              <kbd className="hidden md:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                <span className="text-xs">⌘</span>K
              </kbd>
            </button>
          )}
        </div>
      </div>
      {!isMobile || !isSearchActive ? (
        <div className="flex items-center gap-2">
          {initialLoading ? (
            <>
              <Skeleton className="h-9 w-9 rounded-full" />
              <Skeleton className="h-9 w-9 rounded-full" />
            </>
          ) : (
            <>
              <ThemeToggle />
              <NotificationCenter />
            </>
          )}
        </div>
      ) : null}
    </header>
  );
}

// ─── Theme toggle (light/dark) — local via next-themes, respects system default ─
function ThemeToggle() {
  const { theme, resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return <Skeleton className="h-9 w-9 rounded-full" />;
  const isDark = (resolvedTheme || theme) !== 'light';
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-9 w-9 rounded-full"
      aria-label="Toggle theme"
      title={isDark ? 'Switch to light' : 'Switch to dark'}
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
    >
      <HugeiconsIcon icon={isDark ? Sun : Moon} className="h-4 w-4" strokeWidth={1.5} />
    </Button>
  );
}
